import { useState, useEffect } from 'react'
import { Save, AlertCircle } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useLabelCurral } from '../../hooks/useLabelCurral'
import fichaLeiloeiroService from '../../services/fichaLeiloeiroService'

export default function FichaLeiloeiroConfig() {
  const { organizadorAtivo } = useOrganizador()
  const labelCurral = useLabelCurral()
  const [config, setConfig] = useState({
    mostrar_vendedor: true,
    mostrar_cidade: true,
    mostrar_ordem: true,
    mostrar_lote: true,
    mostrar_qtd: true,
    mostrar_tipo: true,
    mostrar_raca: true,
    mostrar_curral: true,
    mostrar_sexo: true,
    mostrar_idade: true,
    mostrar_peso: true,
    mostrar_observacoes: true,
    fichas_por_folha: 1,
    mostrar_logo: true,
    versao_ficha: 'v1'
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const handleCampoChange = (campo) => {
    setConfig(prev => ({
      ...prev,
      [`mostrar_${campo}`]: !prev[`mostrar_${campo}`]
    }))
  }

  useEffect(() => {
    if (organizadorAtivo?.id) {
      loadConfig()
    }
  }, [organizadorAtivo])

  const loadConfig = async () => {
    try {
      const response = await fichaLeiloeiroService.getConfig(organizadorAtivo.id)
      setConfig(response)
    } catch (error) {
      console.error('Erro ao carregar configuração:', error)
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      await fichaLeiloeiroService.saveConfig(organizadorAtivo.id, config)
      setMessage('Configurações salvas com sucesso!')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      console.error('Erro ao salvar configurações:', error)
      setMessage('Erro ao salvar configurações')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-3xl font-bold text-white">
                Configuração - Ficha do Leiloeiro
              </h1>
              <p className="text-orange-100 mt-1 text-sm sm:text-base">
                Configure os campos e layout das fichas do leiloeiro
              </p>
            </div>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="bg-white shadow-sm rounded-xl border border-gray-100">
          <div className="p-6">
            {/* Campos Visíveis */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Campos Visíveis
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  'vendedor', 'cidade', 'ordem', 'tipo',
                  'raca', 'curral', 'sexo', 'idade', 'peso', 'observacoes'
                ].map((campo) => (
                  <label key={campo} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config[`mostrar_${campo}`]}
                      onChange={() => handleCampoChange(campo)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 capitalize">
                      {campo === 'curral' ? labelCurral : campo.replace('_', ' ')}
                    </span>
                  </label>
                ))}
                {/* Campos obrigatórios */}
                <div className="col-span-full border-t pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Campos Obrigatórios (sempre visíveis)</h4>
                  <div className="flex gap-4">
                    <label className="flex items-center space-x-2 opacity-75">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled={true}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Lote</span>
                    </label>
                    <label className="flex items-center space-x-2 opacity-75">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled={true}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Quantidade</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Layout */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Layout de Impressão
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Versão da Ficha
                  </label>
                  <select
                    value={config.versao_ficha}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      versao_ficha: e.target.value
                    }))}
                    className="block w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="v1">Versão 1 - Padrão</option>
                    <option value="v2">Versão 2 - Com Comprador/Fazenda/Prazo</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    A versão 2 adiciona campos de Comprador, Fazenda e Prazo na parte inferior da ficha.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fichas por folha
                  </label>
                  <select
                    value={config.fichas_por_folha}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      fichas_por_folha: Number(e.target.value)
                    }))}
                    className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={1}>1 ficha por folha</option>
                    <option value={2}>2 fichas por folha</option>
                    <option value={3}>3 fichas por folha</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.mostrar_logo}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        mostrar_logo: e.target.checked
                      }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      Mostrar logo do organizador na impressão
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Informações */}
            <div className="mb-8 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sobre as Fichas do Leiloeiro:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Cada ficha representa um lote do leilão</li>
                    <li>Layout retangular com bordas bem definidas</li>
                    <li>Campos organizados em cabeçalho, dados e observações</li>
                    <li>Ideal para anotações durante o leilão</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <div>
                {message && (
                  <span className={`text-sm ${
                    message.includes('sucesso') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {message}
                  </span>
                )}
              </div>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          </div>
        </div>
    </div>
  )
}
